/**
 * English UI catalogue for namespace 'batch' (W2-11 batch-tools; defineMessages + catalogOf; imports only
 * src/i18n/define.ts). Tool failure reasons and notes produced by core live in src/core/tools/tools.messages.ts
 * (namespace 'tools').
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('batch', {
  // ---- Units (shown after numbers and accepted when typed, e.g. "25px": keep the expression language's unit
  // symbol unless the field should accept a different suffix)
  unitPx: 'px',

  // ---- Commands
  cmdDistribute: 'Distribute values (ramp)...',
  cmdClone: 'Clone to lines...',
  cmdFill: 'Fill between two notes...',
  cmdPath: 'Motion path...',
  cmdBake: 'Bake or simplify segments...',
  cmdFlatten: 'Flatten layers...',
  cmdStagger: 'Stagger selection...',
  cmdTransform: 'Transform...',
  cmdReverseTime: 'Reverse in time',
  cmdStretch: 'Stretch time to double length',
  cmdSqueeze: 'Squeeze time to half length',
  cmdExtrapolate: 'Extrapolate from the previous two segments',
  cmdJoin: 'Join segments',
  cmdReverse: 'Reverse segments',
  cmdLink: 'Link segments',
  cmdUnlink: 'Unlink segments',
  cmdSelectLinked: 'Select linked segments',
  cmdSplitRpe: 'Split at playhead (RPE linear)',
  cmdBakeQuick: 'Bake to straight pieces',
  cmdSelectSimilar: 'Select similar',
  cmdRangeStart: 'Range start here',
  cmdRangeEnd: 'Range end here (select the range)',
  cmdEveryNth: 'Keep every Nth selected item...',
  cmdInvert: 'Invert selection',
  cmdPasteSpecial: 'Paste special...',

  // ---- Transaction labels
  txnDistribute: 'Distribute values',
  txnClone: 'Clone to lines',
  txnFill: 'Fill between notes',
  txnPath: 'Motion path',
  txnBake: 'Bake segments',
  txnSimplify: 'Simplify segments',
  txnFlatten: 'Flatten layers',
  txnStagger: 'Stagger',
  txnTransform: 'Transform',
  txnReverseTime: 'Reverse in time',
  txnStretch: 'Stretch time',
  txnSqueeze: 'Squeeze time',
  txnExtrapolate: 'Extrapolate segments',
  txnJoin: 'Join segments',
  txnReverse: 'Reverse segments',
  txnLink: 'Link segments',
  txnUnlink: 'Unlink segments',
  txnSplitRpe: 'Split segment (RPE linear)',
  txnPasteSpecial: 'Paste special',

  // ---- Disabled reasons
  reasonNoProject: 'Open a project first.',
  reasonNothingSelected: 'Select notes or keyframes first.',
  reasonNoSegments: 'Select one or more segments first.',
  reasonNeedTwoSegments: 'Select at least two segments.',
  reasonJoinSelect: 'Select the keyframe between two segments, or two neighbouring segments.',
  reasonNotLinked: 'None of the selected segments is linked.',
  reasonNoColumn: 'Click a motion column (or a segment) first, so it is clear which property to split.',
  reasonPlayheadOutside: 'Move the playhead inside a segment of this property.',
  reasonNoLine: 'Choose a line first.',
  reasonNoRangeStart: 'Set the range start first (Range start at playhead).',

  // ---- Shared sheet words
  apply: 'Apply',
  generate: 'Generate',
  cancel: 'Cancel',
  paste: 'Paste',
  copy: 'Copy',
  previewPaused: 'Preview paused by another edit. Change a value or apply to continue.',
  countNotes: '{count, plural, one {# note} other {# notes}}',
  countKeys: '{count, plural, one {# keyframe} other {# keyframes}}',
  countSegments: '{count, plural, one {# segment} other {# segments}}',
  countNotesKeys: '{notes, plural, one {# note} other {# notes}}, {keys, plural, one {# keyframe} other {# keyframes}}',
  countKeysNotes: '{keys, plural, one {# segment} other {# segments}}, {notes, plural, one {# note} other {# notes}}',
  curve: 'Curve',
  moreCurves: 'More curves...',
  curveNumber: 'Curve {id}',
  curvePlain: '{strength}, {shape}',
  curvePart: '{name} (part {left} to {right})',
  from: 'From',
  to: 'To',
  pattern: 'Pattern',
  patternPlaceholder: 'for example 1 -1',
  patternInvalid: 'The pattern must be numbers separated by spaces, for example 1 -1.',
  jitter: 'Random +/-',
  seed: 'Seed',
  newSeed: 'New random seed',
  tolerance: 'Tolerance',
  equivalentExpression: 'Equivalent expression',
  expressionCopied: 'Expression copied.',
  copyFailed: 'The browser did not allow copying.',
  valueNone: 'none',
  xOffset: 'X offset',

  // ---- Distribute
  distributeTitle: 'Distribute / Ramp',
  distributeNeedsItems: 'Select notes or keyframes to distribute values over.',
  applyTo: 'Apply to',
  targetNotes: 'Notes',
  targetKeys: 'Keyframes',
  change: 'Change',
  order: 'Order',
  orderTime: 'Time',
  orderX: 'X',
  fieldX: 'X position',
  fieldSpeed: 'Speed',
  fieldSize: 'Width',
  fieldYOffset: 'Height offset',
  fieldVisibleTime: 'Appear time (s)',
  fieldAlpha: 'Opacity (0-255)',
  fieldTime: 'Time (beats)',
  fieldValue: 'Value',
  fieldValueIn: 'Arrive value',
  fieldValueOut: 'Leave value',
  fieldEasing: 'Curve number',
  how: 'How',
  modeSet: 'Set',
  modeAdd: 'Add',
  modeMultiply: 'Multiply',
  modeMax: 'Max',
  modeMin: 'Min',
  modeMirror: 'Mirror',
  expressionTimeOrder:
    'The expression applies items in time order; this ramp uses X order, so typing it in a field gives a different result.',
  previewList: 'First items, before and after',
  previewItem: '#{id}  {before} -> {after}',
  distributeChanged: '{count, plural, one {# item changes} other {# items change}}.',
  distributeDone: 'Distributed values over {count, plural, one {# item} other {# items}}.',

  // ---- Clone
  cloneTitle: 'Clone to lines',
  cloneTargets: 'Targets',
  cloneTargetLines: 'Pick lines',
  cloneTargetCreate: 'Create new lines',
  cloneNoOtherLines: 'There are no other lines yet. Create new lines instead.',
  sourceLine: '{line} (source)',
  clonePickTargets: 'Tick the lines to clone to.',
  cloneCount: 'How many',
  cloneNamePattern: 'Names',
  cloneNameHint: '{k} is replaced by 0, 1, 2 ... and {n} by 1, 2, 3 ...',
  copyParent: 'Same parent as the source',
  copyZ: 'Same draw order',
  copyGroup: 'Same group',
  copyType: 'Same type (image, text, UI mover...)',
  cloneStagger: 'Stagger per target (beats)',
  cloneExpressionNote:
    'Added to every value copied to a target; u is 0 for the first target and 1 for the last (the same engine as Distribute).',
  clonePerTarget: 'Add per target (from the first to the last target)',
  rampX: 'X',
  rampY: 'Y',
  rampRotate: 'Rotation',
  rampAlpha: 'Opacity',
  rampFrom: 'First',
  rampTo: 'Last',
  rampMore: 'Curves, patterns and randomness',
  cloneOverlap: 'Where a target already moves',
  overlapReplace: 'Replace',
  overlapSkip: 'Skip',
  overlapNewLayer: 'Add as a new layer',
  overlapRefuse: 'Refuse',
  cloneCopyNotes: 'Copy notes',
  cloneNoNotes: 'No notes are selected.',
  cloneKeepSource: 'Keep the source',
  cloneNeedsSource: 'Select the segments (and notes) to clone first.',
  cloneNeedsTargets: 'Choose target lines or create new ones.',
  cloneStatus: 'Clones to {count, plural, one {# line} other {# lines}}.',
  cloneDone: 'Cloned to {count, plural, one {# line} other {# lines}}.',

  // ---- Fill
  fillTitle: 'Fill between',
  fillNeedTwo: 'Select two notes: where the fill starts and where it ends.',
  fillSameLine: 'Both notes must be on the same line.',
  fillHowTo: 'Select the start and end notes in the lane (Ctrl+click the second), then open Fill between again.',
  fillPickOne: 'Select exactly one note to use as the start of the fill.',
  fillHeadSet: 'Fill start remembered. Select the end note and pick it as the end.',
  fillPickTailHint: 'picks the selected note as the end of the fill',
  cmdCloseSheet: 'Close the tool sheet',
  cmdSelectNone: 'Clear the selection',
  reasonNothingToClear: 'Nothing is selected',
  reasonNoSheet: 'No tool sheet is open',
  fillPickHeadFirst: 'Pick the start note first, then select the end note.',
  noteType: 'Note type',
  kindTap: 'Tap',
  kindDrag: 'Drag',
  kindFlick: 'Flick',
  kindHold: 'Hold',
  spacing: 'Spacing',
  spacingEvery: 'Every',
  spacingCount: 'Count',
  spacingEveryBeat: 'One note every',
  beatFraction: '{beat} beat',
  spacingCountLabel: 'Notes between',
  xCurve: 'X curve',
  includeEnds: 'Turn the two end notes into this type too',
  propsFrom: 'Properties from',
  propsStart: 'Start note',
  propsDefaults: 'New-note defaults',
  fillHint: 'X follows the curve from the start note to the end note.',
  fillExpressionNote:
    'X of each new note; t is the note\u2019s beat. Type it into the X field of other notes to shape them the same way.',
  fillStatus: '{count, plural, one {# note} other {# notes}}.',
  fillDone: 'Filled {count, plural, one {# note} other {# notes}}.',

  // ---- Motion path
  pathTitle: 'Motion path',
  pathLine: 'Line {line}',
  pathNeedsLine: 'Choose a line for the path.',
  pathNeedsLineBody: 'Make a line active in the Lines panel or click it in the preview, then open Motion path again.',
  preset: 'Shape',
  presetLine: 'Straight line',
  presetArc: 'Arc',
  presetCircle: 'Circle',
  presetSpiral: 'Spiral',
  presetFigure8: 'Figure 8',
  presetSine: 'Wave',
  presetExpr: 'Expressions',
  pathStart: 'Start beat',
  pathEnd: 'End beat',
  fromX: 'From X',
  fromY: 'From Y',
  toX: 'To X',
  toY: 'To Y',
  centerX: 'Centre X',
  centerY: 'Centre Y',
  radius: 'Radius',
  radiusStart: 'Start radius',
  radiusEnd: 'End radius',
  fromDeg: 'From angle (deg)',
  toDeg: 'To angle (deg)',
  startDeg: 'Start angle (deg)',
  turns: 'Turns',
  width: 'Width',
  height: 'Height',
  amplitude: 'Amplitude',
  waves: 'Waves',
  exprX: 'x(t)',
  exprY: 'y(t)',
  exprR: 'Rotation r(t)',
  exprROptional: 'optional, degrees',
  exprHelp: 't goes from 0 to 1 over the path (after the time curve); beat is the chart beat. RPE $t$ and Pi work too.',
  editAsExpressions: 'Edit as expressions',
  timeCurve: 'Time curve',
  output: 'Output',
  outputLinear: 'Keyframes per beat',
  outputFit: 'Fit to tolerance',
  keysPerBeat: 'Keyframes per beat',
  tolerancePx: 'Largest allowed error',
  layer: 'Layer',
  layerNew: 'New layer ({layer})',
  layerNewNone: 'New layer (none free)',
  layerUsed: 'Layer {layer} (in use)',
  layerFree: 'Layer {layer} (empty)',
  pathOverlap: 'Existing motion in the span',
  pathRangeInvalid: 'The end must come after the start.',
  pathExprInvalid: 'Fix the {axis} expression.',
  pathFixFields: 'Fix the highlighted fields first.',
  pathHint:
    'On a new layer the path adds to the line\u2019s own motion. The path is drawn in the preview, green at the start and red at the end.',
  pathFromLine: 'Start from the line\u2019s position',
  pathFromLineHint:
    'The path is moved so it starts where the line already is: no jump at the start beat, and closed shapes (full circles, figure-8) end where they started.',
  pathAbsoluteHint:
    'The path\u2019s coordinates are added to the line\u2019s position as they are: the line jumps to the path\u2019s first point at the start beat and keeps the last point\u2019s offset after the end.',
  pathAbsoluteLayerHint:
    'Over the span, layer {layer} takes the path\u2019s coordinates instead of its own values: it jumps to the path\u2019s first point at the start beat, and its curve before and after the span stays as it was.',
  pathCentreIgnored: 'The centre makes no difference while the path starts from the line\u2019s position.',
  pathEndsRelative:
    'Only the step from From to To counts while the path starts from the line\u2019s position: the path is moved so its From point is where the line is.',
  appliedExpressions: 'Applied expressions',
  pathStatus: '{keys} keyframes per axis, largest error {error} px.',
  pathDone: 'Motion path written ({keys} keyframes).',

  // ---- Bake / simplify
  bakeTitle: 'Bake or simplify',
  bakeHowTo: 'Select segments in a motion column, then open this tool.',
  bakeMode: 'Action',
  bakeModeBake: 'Bake to straight pieces',
  bakeModeSimplify: 'Simplify (fit curves)',
  bakeDensity: 'Pieces per grid step',
  bakeDensityHelp: 'One piece every 1/{div} beat.',
  bakeAnchor: 'Line up pieces with',
  bakeAnchorPlayhead: 'Grid at the playhead',
  bakeAnchorStart: 'Segment start',
  bakePartner: 'Also bake the other position axis over the same time',
  bakeHint: 'Values are sampled from the exact curve. X and Y stay keyed together on lines where they are linked.',
  simplifyHint: 'Many short keyframes become fewer curved segments that stay within the tolerance.',
  bakeFormulaStart: 'segment start',
  bakeFormulaNote:
    'One keyframe every 1/{div} beat, valued from the exact curve and joined by Linear. Without this panel, \u201cBake to straight pieces\u201d uses the density from Settings.',
  simplifyFormulaNote: 'Keyframes merge into fewer eased segments while the new curve stays this close to the old one.',
  bakeStatus: '{count, plural, one {# piece} other {# pieces}}.',
  simplifyStatus: 'Removes {removed, plural, one {# keyframe} other {# keyframes}}, largest difference {error}.',
  bakeDone: 'Baked into {count, plural, one {# piece} other {# pieces}}.',
  simplifyDone: 'Simplified: {count, plural, one {# keyframe} other {# keyframes}} removed.',

  // ---- Flatten
  flattenTitle: 'Flatten layers',
  flattenSubtitle: '{prop} of {line}',
  flattenLayers: 'Layers to merge',
  flattenLayerItem: 'Layer {layer}: {keys, plural, one {# keyframe} other {# keyframes}}',
  flattenHint: 'Exact wherever the layers can be combined; elsewhere short straight pieces within the tolerance.',
  flattenFormulaTerm: 'layer {layer}',
  flattenFormulaNote: 'At every beat, layer 0 becomes the sum of the layers; the other layers are removed.',
  flattenExactStatus: 'Exact: {keys} keyframes on layer 0.',
  flattenBakedStatus: '{keys} keyframes on layer 0, largest difference {error}.',
  flattenDone: 'Merged {count, plural, one {# layer} other {# layers}} into layer 0.',
  flattenNothing: 'No property of this line has more than one layer.',
  flattenChoose: 'Flatten which property?',
  flattenOneLayer: 'This property has a single layer: there is nothing to flatten.',
  flattenNotLayered: 'Only position, rotation, opacity and scroll speed have layers.',
  propMoveX: 'Position X',
  propMoveY: 'Position Y',
  propRotate: 'Rotation',
  propAlpha: 'Opacity',
  propSpeed: 'Scroll speed',
  propScaleX: 'Length / width',
  propScaleY: 'Thickness / height',
  propColor: 'Colour',
  propText: 'Text',
  propIncline: 'Note tilt',
  propPaint: 'Brush',
  propGif: 'GIF progress',

  // ---- Stagger
  staggerStep: 'Stagger step',
  staggerBody: 'Each next time in the selection moves by one more step.',
  staggerOption: '{beat} beat',
  staggerNeedsTwo: 'Select items at two or more times.',
  staggerDone: 'Staggered {count, plural, one {# item} other {# items}} by {beat} beat.',

  // ---- Segment operations
  joinDone: 'Joined {count, plural, one {# keyframe} other {# keyframes}}.',
  joinSkipped:
    '{count, plural, one {# keyframe} other {# keyframes}} could not be joined exactly. Simplify can merge them within a tolerance.',
  simplifyInstead: 'Simplify...',
  reverseDone: 'Reversed {count, plural, one {# segment} other {# segments}}.',
  linkDone: 'Linked {count, plural, one {# segment} other {# segments}}: editing one now edits all.',
  selectedLinked: '{count, plural, one {# linked segment} other {# linked segments}} selected.',
  splitRpeDone: 'Split with RPE’s linear rule (the curve after the split changes shape).',

  // ---- Selection
  similarLine:
    'Selected {count, plural, one {# similar item} other {# similar items}} on this line. Press again for all lines.',
  similarAllLines: 'Selected {count, plural, one {# similar item} other {# similar items}} on every line.',
  rangeStartSet: 'Range starts at {beat}. Right-click where it ends (or move the playhead) and choose Range end.',
  rangeSelected:
    'Selected {notes, plural, one {# note} other {# notes}} and {keys, plural, one {# keyframe} other {# keyframes}}.',
  everyNthArg: 'Keep every Nth item',
  everyNthOption: 'Every {n, plural, one {#} =2 {2nd} =3 {3rd} other {#th}} item',

  // ---- Transform
  transformTitle: 'Transform',
  transformDescription: 'Move and scale {count, plural, one {# item} other {# items}} exactly.',
  transformTime: 'Time',
  transformX: 'X (notes)',
  timeOffset: 'Move by (beats)',
  timeScale: 'Scale time',
  scaleNone: 'Unchanged',
  scaleStretch: 'Stretch {p}/{q}',
  scaleSqueeze: 'Squeeze {p}/{q}',
  scaleCustom: 'Custom ratio',
  scaleP: 'Times',
  scaleQ: 'Divided by',
  timePivot: 'Around',
  pivotStart: 'Start ({beat})',
  pivotEnd: 'End ({beat})',
  pivotPlayhead: 'Playhead ({beat})',
  xScale: 'X scale',
  xPivot: 'X scale around',
  xPivotZero: 'Centre of the line (0)',
  xPivotCenter: 'Selection centre ({x})',
  transformXHint: 'X changes apply to notes; keyframes move in time only.',
  transformNothing: 'Select notes or keyframes to transform.',
  transformStatus: 'Transforms {count, plural, one {# item} other {# items}}.',
  transformDone: 'Transformed {count, plural, one {# item} other {# items}}.',
  reverseTimeDone: 'Reversed {count, plural, one {# item} other {# items}} in time.',

  // ---- Paste special
  pasteSpecialTitle: 'Paste special',
  pasteMode: 'Paste as',
  pasteNormal: 'Normal',
  pasteAddValues: 'Add values',
  pasteNewLayer: 'New layer',
  pasteNeedsKeys: 'The clipboard holds no keyframes.',
  pasteMirror: 'Mirrored (X and values negated)',
  pasteAnchor: 'Place at',
  anchorMouse: 'Mouse',
  anchorPlayhead: 'Playhead',
  anchorOriginal: 'Original beats',
  anchorMouseShort: 'at the mouse',
  anchorPlayheadShort: 'at the playhead',
  anchorOriginalShort: 'at the original beats (from {beat})',
  pasteTargetLine: 'Target line',
  pasteTargets: 'Lines',
  targetCurrent: 'Target line',
  targetOriginal: 'Original lines',
  originalLines: 'their original lines',
  repeat: 'Times',
  repeatEvery: 'Every (beats)',
  repeatEveryInvalid: 'The repeat distance must be longer than zero.',
  pasteSummary:
    '{kind, select, addValues {Adds the values of} asNewLayer {Pastes as a new layer} other {Pastes}} {notes, plural, one {# note} other {# notes}} and {keys, plural, one {# keyframe} other {# keyframes}} to {line} {anchor}{repeat, plural, one {} other {, # times every {every} beat}}{x, select, 0 {} other {, X {x}}}{mirror, select, yes {, mirrored} other {}}.',
  clipboardEmpty: 'The clipboard is empty. Copy notes or keyframes first.',
  pasteDone:
    'Pasted {notes, plural, one {# note} other {# notes}} and {keys, plural, one {# keyframe} other {# keyframes}} at {beat}.',

  // ---- Settings
  settingBakeDensity: 'Bake: pieces per grid step',
  settingBakeDensityHelp: 'How finely "Bake to straight pieces" cuts a segment (RPE Cut density). Default 8.',
  settingPathPerBeat: 'Motion path: keyframes per beat',
  settingPathPerBeatHelp: 'Starting density of the Motion path tool. Default 8.',

  // ---- Help
  helpToolsTitle: 'Batch tools',
  helpToolsBody:
    'Select notes or keyframes, then open a tool from the Tools menu, the right-click menu or the command palette. While its panel is open the result shows live in the lane, the motion columns and the preview; Enter applies it as one undoable step and Esc cancels. Each tool shows the expression or settings it uses, so you can repeat it by hand.',
  helpDistributeTitle: 'Distribute and ramp values',
  helpDistributeBody:
    'Distribute spreads values over the selected items in time order: from a first to a last value along a curve, optionally multiplied by a repeating pattern (1 -1 alternates the sign) and with seeded randomness. Set replaces the values; Add, Multiply, Max, Min and Mirror combine them with the current values. RPE: MultiNote / MultiEvent editor.',
  helpPathTitle: 'Motion paths',
  helpPathBody:
    'Motion path moves a line along a shape (circle, arc, spiral, wave...) or along expressions x(t) and y(t) between two beats. It writes straight keyframes on a new layer by default, so the line’s own motion keeps playing underneath. RPE: Complex move.',
  helpFlattenTitle: 'Layers and flattening',
  helpFlattenBody:
    'Position, rotation, opacity and speed can have several layers that add up. Flatten merges them into layer 0, exactly where possible, and otherwise into short straight pieces within the tolerance you set.',
});

export default catalogOf(m);
